import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory } from 'lucide-react';

// Product Summary Tab Component
export const ProductSummaryTab = ({ orders = [], onNavigate, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [dealerFilterId, setDealerFilterId] = useState('');
  const [dealerSearch, setDealerSearch] = useState('');
  const [dealerDropdownOpen, setDealerDropdownOpen] = useState(false);
  const todayPS = new Date().toISOString().slice(0, 10);
  const tomorrowPS = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  // Default preset: Today
  const [datePreset, setDatePreset] = useState('today'); // 'today' | 'tomorrow' | 'edit'
  const [dateFrom, setDateFrom] = useState(todayPS);
  const [dateTo, setDateTo] = useState(todayPS);

  const applyPreset = (key) => {
    setDatePreset(key);
    if (key === 'today') { setDateFrom(todayPS); setDateTo(todayPS); }
    else if (key === 'tomorrow') { setDateFrom(tomorrowPS); setDateTo(tomorrowPS); }
    // 'edit' keeps current values; date inputs become visible
  };
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [psReviewOrder, setPsReviewOrder] = useState(null);
  const [psReviewOrders, setPsReviewOrders] = useState(null); // { orders: [], product_id, product_name, dealer_name }
  const [groupByParent, setGroupByParent] = useState(true); // Stack MISC sub-brands under their parent product
  const [expandedSub, setExpandedSub] = useState(null); // Key: `${parent_id}::${sub_brand_name}` — which sub-brand row is expanded

  const fmtDatePS = (d) => d ? new Date((d.includes('T') ? d : d + 'T00:00:00')).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  // Monotonic request counter — drops stale concurrent fetches so the
  // latest filter response always wins (race-condition guard).
  const fetchSummarySeqRef = React.useRef(0);

  const fetchSummary = async () => {
    const seq = ++fetchSummarySeqRef.current;
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dealerFilterId) params.dealer_id = dealerFilterId;
      if (groupByParent) params.group_by_parent = true;
      // Cache-bust: ensure no intermediate proxy (browser, CDN) serves a stale snapshot.
      // The backend ignores this query param.
      params._t = Date.now();
      const res = await analyticsAPI.getProductSummary(params);
      if (seq !== fetchSummarySeqRef.current) return;  // stale — drop
      setData(res.data);
    } catch (error) {
      if (seq === fetchSummarySeqRef.current) toast.error('Failed to load product summary');
    } finally {
      if (seq === fetchSummarySeqRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [dateFrom, dateTo, statusFilter, groupByParent, dealerFilterId]);

  // Silent refresh on tab re-entry: parent bumps `refreshKey` and we re-pull
  // the summary without resetting the date range, status filter, group-by
  // toggle, dealer filter, search input, or sort. Skips the initial render
  // via a useRef sentinel since the [dateFrom, dateTo, ...] effect above
  // already covers the first mount. Per user request (20/Feb/2026).
  const isFirstSummaryRefreshRef = React.useRef(true);
  useEffect(() => {
    if (isFirstSummaryRefreshRef.current) { isFirstSummaryRefreshRef.current = false; return; }
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Multi-token search over name/town/phone (case-insensitive, AND of tokens)
  const filteredDealersForPicker = useMemo(() => {
    const q = (dealerSearch || '').trim().toLowerCase();
    if (!q) return dealers.slice(0, 50);
    const tokens = q.split(/\s+/).filter(Boolean);
    return dealers.filter(d => {
      const hay = `${d.name || ''} ${d.town || ''} ${d.phone_number || ''}`.toLowerCase();
      return tokens.every(t => hay.includes(t));
    }).slice(0, 50);
  }, [dealers, dealerSearch]);

  const selectedDealer = dealers.find(d => d.id === dealerFilterId);

  // Load dealers once for the dealer-filter dropdown
  useEffect(() => {
    dealerAPI.getAll().then(r => setDealers(r.data || [])).catch(() => {});
  }, []);

  // Refetch whenever the user returns to the tab/window so the figures reflect any
  // orders placed/edited in another tab without a manual reload.
  useEffect(() => {
    const onFocus = () => fetchSummary();
    const onVisible = () => { if (document.visibilityState === 'visible') fetchSummary(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, statusFilter, groupByParent]);

  return (
    <div data-testid="product-summary-section">
      <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6" style={{ color: '#2B5339' }}>Product-wise Order Summary</h2>

      {/* Filters (Today / Tomorrow / Edit + status + group toggle) */}
      <Card className="p-5 bg-white border mb-6" style={{ borderColor: '#E2DFD8' }}>
        <p className="text-xs mb-3" style={{ color: '#5C6B61' }}>Dates refer to the <b>Schedule Date</b> (delivery date) of orders.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Schedule Date</label>
            <div className="flex gap-1.5">
              {[
                { key: 'today', label: 'Today' },
                { key: 'tomorrow', label: 'Tomorrow' },
                { key: 'edit', label: 'Edit' },
              ].map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  className="text-xs px-3 py-2 rounded font-medium border transition-colors h-10"
                  style={datePreset === p.key
                    ? { background: '#2B5339', color: 'white', borderColor: '#2B5339' }
                    : { background: 'white', color: '#2B5339', borderColor: '#E2DFD8' }}
                  data-testid={`ps-date-preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {datePreset === 'edit' && (
            <>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>From</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-44" data-testid="ps-filter-date-from" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>To</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-44" data-testid="ps-filter-date-to" />
              </div>
            </>
          )}
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Order Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded text-sm h-10 w-40" style={{ borderColor: '#E2DFD8' }} data-testid="ps-filter-status">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="dispatched">Dispatched + Delivered</option>
            </select>
          </div>
          <div className="relative">
            <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Dealer</label>
            {selectedDealer ? (
              <div className="flex items-center gap-2 h-10 px-3 rounded border text-sm" style={{ borderColor: '#2B5339', background: '#EFF4EE' }} data-testid="ps-dealer-filter-active">
                <span className="font-medium" style={{ color: '#2B5339' }}>
                  {selectedDealer.name}
                  {selectedDealer.town && <span className="font-normal" style={{ color: '#5C6B61' }}> · {selectedDealer.town}</span>}
                </span>
                <button
                  type="button"
                  onClick={() => { setDealerFilterId(''); setDealerSearch(''); }}
                  className="ml-1 text-xs px-1.5 py-0.5 rounded hover:bg-white"
                  style={{ color: '#C05C35' }}
                  data-testid="ps-dealer-filter-clear"
                  title="Clear dealer filter"
                >
                  ×
                </button>
              </div>
            ) : (
              <>
                <Input
                  type="text"
                  placeholder="Search dealer name / town / phone…"
                  value={dealerSearch}
                  onChange={(e) => { setDealerSearch(e.target.value); setDealerDropdownOpen(true); }}
                  onFocus={() => setDealerDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setDealerDropdownOpen(false), 150)}
                  className="h-10 w-64"
                  data-testid="ps-dealer-filter-input"
                />
                {dealerDropdownOpen && filteredDealersForPicker.length > 0 && (
                  <div className="absolute z-20 mt-1 w-64 max-h-60 overflow-auto bg-white border rounded shadow-lg" style={{ borderColor: '#E2DFD8' }} data-testid="ps-dealer-filter-dropdown">
                    {filteredDealersForPicker.map(d => (
                      <button
                        key={d.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setDealerFilterId(d.id); setDealerSearch(''); setDealerDropdownOpen(false); }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#FBF9F6] border-b last:border-b-0"
                        style={{ borderColor: '#F0EDE7' }}
                        data-testid="ps-dealer-filter-option"
                      >
                        <span className="font-medium">{d.name}</span>
                        {d.town && <span className="ml-1" style={{ color: '#5C6B61' }}>· {d.town}</span>}
                        {d.phone_number && <span className="ml-1 text-[10px]" style={{ color: '#B9BEB6' }}>{d.phone_number}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer h-10 px-3 rounded border" style={{ borderColor: groupByParent ? '#2B5339' : '#E2DFD8', background: groupByParent ? '#EFF4EE' : 'white' }} title="When ON, MISC sub-brand line items roll up under their parent product." data-testid="ps-group-by-parent-toggle">
            <input
              type="checkbox"
              checked={groupByParent}
              onChange={(e) => setGroupByParent(e.target.checked)}
              className="cursor-pointer"
              data-testid="ps-group-by-parent-checkbox"
            />
            <span style={{ color: groupByParent ? '#2B5339' : '#5C6B61' }}>Stack MISC under parent</span>
          </label>
          <Button
            type="button"
            variant="outline"
            onClick={fetchSummary}
            disabled={loading}
            className="h-10"
            title="Reload the summary from the server (skips any cached snapshot)"
            data-testid="ps-refresh-btn"
          >
            <Loader2 className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2B5339' }} />
        </div>
      ) : data ? (
        <>
          {/* Product Table — minimal columns */}
          <Card className="p-6 bg-white border" style={{ borderColor: '#E2DFD8' }}>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#2B5339' }}>Product Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ background: '#FBF9F6' }}>
                    <th className="text-left p-3 text-sm font-semibold" style={{ color: '#5C6B61' }}>S.No.</th>
                    <th className="text-left p-3 text-sm font-semibold" style={{ color: '#5C6B61' }}>Product</th>
                    <th className="text-right p-3 text-sm font-semibold" style={{ color: '#5C6B61' }}>Total Qty</th>
                    <th className="text-center p-3 text-sm font-semibold" style={{ color: '#5C6B61' }}>Pending</th>
                    <th className="text-center p-3 text-sm font-semibold" style={{ color: '#5C6B61' }}>Confirmed</th>
                    <th className="text-center p-3 text-sm font-semibold" style={{ color: '#5C6B61' }}>Dispatched</th>
                  </tr>
                </thead>
                <tbody>
                  {data.product_stats.map((product, idx) => (
                    <React.Fragment key={product.product_id}>
                      <tr
                        style={{ borderBottom: expandedProduct === product.product_id ? 'none' : '1px solid #E2DFD8', cursor: 'pointer', background: idx % 2 === 0 ? '#FFFFFF' : '#FBF9F6' }}
                        onClick={() => setExpandedProduct(expandedProduct === product.product_id ? null : product.product_id)}
                        className="hover:bg-[#F5F1E8] transition-colors"
                        data-testid="ps-product-row"
                      >
                        <td className="p-3 text-sm">{idx + 1}</td>
                        <td className="p-3 text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <span className="transition-transform" style={{ transform: expandedProduct === product.product_id ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', fontSize: '10px', color: '#5C6B61' }}>&#9654;</span>
                            {product.product_name}
                            {product.dealers && <span className="text-xs ml-1 px-1.5 py-0.5 rounded" style={{ background: '#FBF9F6', color: '#5C6B61' }}>{product.dealers.length} dealer{product.dealers.length !== 1 ? 's' : ''}</span>}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-right font-bold" style={{ color: '#2B5339' }}>{product.total_quantity}</td>
                        <td className="p-3 text-sm text-center">
                          {product.pending_qty > 0 && <span className="inline-block px-2 py-1 rounded text-xs font-medium" style={{ background: '#E89D4F', color: 'white' }}>{product.pending_qty}</span>}
                          {product.pending_qty === 0 && <span className="text-xs" style={{ color: '#E2DFD8' }}>-</span>}
                        </td>
                        <td className="p-3 text-sm text-center">
                          {product.confirmed_qty > 0 && <span className="inline-block px-2 py-1 rounded text-xs font-medium" style={{ background: '#2B5339', color: 'white' }}>{product.confirmed_qty}</span>}
                          {product.confirmed_qty === 0 && <span className="text-xs" style={{ color: '#E2DFD8' }}>-</span>}
                        </td>
                        <td className="p-3 text-sm text-center">
                          {product.dispatched_qty > 0 && <span className="inline-block px-2 py-1 rounded text-xs font-medium" style={{ background: '#C05C35', color: 'white' }}>{product.dispatched_qty}</span>}
                          {product.dispatched_qty === 0 && <span className="text-xs" style={{ color: '#E2DFD8' }}>-</span>}
                        </td>
                      </tr>
                      {/* Expanded dealer details */}
                      {expandedProduct === product.product_id && (
                        ((product.dealers && product.dealers.length > 0) || (product.sub_products && product.sub_products.length > 0))
                      ) && (
                        <tr data-testid="ps-dealer-details">
                          <td colSpan="6" className="p-0">
                            <div className="mx-4 mb-4 rounded border" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }}>
                              {/* Sub-Brand stack — only shown when toggle is ON and parent has rolled-up sub-products */}
                              {groupByParent && product.sub_products && product.sub_products.length > 0 && (
                                <div className="border-b" style={{ borderColor: '#E2DFD8' }} data-testid="ps-sub-products-block">
                                  <div className="p-3 border-b" style={{ borderColor: '#E2DFD8', background: '#FFF8F2' }}>
                                    <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#C05C35' }}>
                                      Sub-Brands stacked under {product.product_name}
                                    </p>
                                  </div>
                                  <table className="w-full">
                                    <thead>
                                      <tr style={{ background: '#FFF1E8' }}>
                                        <th className="text-left p-2.5 pl-4 text-xs font-semibold" style={{ color: '#5C6B61' }}>Sub-Brand</th>
                                        <th className="text-right p-2.5 text-xs font-semibold" style={{ color: '#5C6B61' }}>Qty</th>
                                        <th className="text-right p-2.5 text-xs font-semibold" style={{ color: '#5C6B61' }}>Amount</th>
                                        <th className="text-center p-2.5 pr-4 text-xs font-semibold" style={{ color: '#5C6B61' }}># Orders</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {product.sub_products.map((sp) => {
                                        const subKey = `${product.product_id}::${sp.sub_brand_name}`;
                                        const isSubOpen = expandedSub === subKey;
                                        const toggleSub = (e) => {
                                          e.stopPropagation();
                                          setExpandedSub(isSubOpen ? null : subKey);
                                        };
                                        const openDealerOrders = (dealer) => {
                                          const ids = new Set(sp.order_ids || []);
                                          const matches = (orders || [])
                                            .filter(o => ids.has(o.id) && o.dealer_id === dealer.dealer_id)
                                            .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
                                          if (matches.length > 0) {
                                            setPsReviewOrders({
                                              orders: matches,
                                              product_id: product.product_id,
                                              product_name: `${product.product_name} — ${sp.sub_brand_name}`,
                                              dealer_name: dealer.dealer_name,
                                              sub_brand_filter: sp.sub_brand_name,
                                            });
                                          }
                                        };
                                        return (
                                          <React.Fragment key={subKey}>
                                            <tr
                                              style={{ borderBottom: isSubOpen ? 'none' : '1px solid #E2DFD8', cursor: 'pointer' }}
                                              className="hover:bg-[#FFF1E8] transition-colors"
                                              onClick={toggleSub}
                                              data-testid="ps-sub-product-row"
                                            >
                                              <td className="p-2.5 pl-4 text-sm font-medium" style={{ color: '#C05C35' }}>
                                                <span className="transition-transform inline-block mr-1" style={{ transform: isSubOpen ? 'rotate(90deg)' : 'rotate(0deg)', fontSize: '9px' }}>&#9654;</span>
                                                {sp.sub_brand_name}
                                                <span className="text-xs ml-2 px-1.5 py-0.5 rounded" style={{ background: '#FFE8DA', color: '#C05C35' }}>{(sp.dealers || []).length} dealer{(sp.dealers || []).length !== 1 ? 's' : ''}</span>
                                              </td>
                                              <td className="p-2.5 text-sm text-right font-bold" style={{ color: '#2B5339' }}>{sp.quantity}</td>
                                              <td className="p-2.5 text-sm text-right" style={{ color: '#5C6B61' }}>{'\u20B9'}{Number(sp.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                              <td className="p-2.5 pr-4 text-sm text-center" style={{ color: '#5C6B61' }}>{sp.order_count}</td>
                                            </tr>
                                            {/* Sub-brand → dealer-wise breakdown */}
                                            {isSubOpen && sp.dealers && sp.dealers.length > 0 && (
                                              <tr data-testid="ps-sub-dealer-details">
                                                <td colSpan="4" className="p-0" style={{ background: '#FFF8F2' }}>
                                                  <table className="w-full">
                                                    <thead>
                                                      <tr style={{ background: '#FFE8DA' }}>
                                                        <th className="text-left p-2 pl-8 text-xs font-semibold" style={{ color: '#5C6B61' }}>Dealer (click to view orders)</th>
                                                        <th className="text-right p-2 text-xs font-semibold" style={{ color: '#5C6B61' }}>Qty</th>
                                                        <th className="text-center p-2 text-xs font-semibold" style={{ color: '#5C6B61' }}>Pending</th>
                                                        <th className="text-center p-2 text-xs font-semibold" style={{ color: '#5C6B61' }}>Confirmed</th>
                                                        <th className="text-center p-2 pr-4 text-xs font-semibold" style={{ color: '#5C6B61' }}>Dispatched</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {sp.dealers.map((de) => (
                                                        <tr
                                                          key={de.dealer_id}
                                                          style={{ borderBottom: '1px solid #F3D8C6', cursor: 'pointer' }}
                                                          className="hover:bg-[#FFE8DA] transition-colors"
                                                          onClick={(e) => { e.stopPropagation(); openDealerOrders(de); }}
                                                          data-testid="ps-sub-dealer-row"
                                                        >
                                                          <td className="p-2 pl-8 text-sm font-medium">{de.dealer_name}<span className="text-xs ml-2" style={{ color: '#5C6B61' }}>{de.dealer_phone}</span></td>
                                                          <td className="p-2 text-sm text-right font-medium" style={{ color: '#2B5339' }}>{de.quantity}</td>
                                                          <td className="p-2 text-sm text-center">{de.pending_qty > 0 ? <span className="inline-block px-1.5 py-0.5 rounded text-xs" style={{ background: '#E89D4F', color: 'white' }}>{de.pending_qty}</span> : <span style={{ color: '#E2DFD8' }}>-</span>}</td>
                                                          <td className="p-2 text-sm text-center">{de.confirmed_qty > 0 ? <span className="inline-block px-1.5 py-0.5 rounded text-xs" style={{ background: '#2B5339', color: 'white' }}>{de.confirmed_qty}</span> : <span style={{ color: '#E2DFD8' }}>-</span>}</td>
                                                          <td className="p-2 pr-4 text-sm text-center">{de.dispatched_qty > 0 ? <span className="inline-block px-1.5 py-0.5 rounded text-xs" style={{ background: '#C05C35', color: 'white' }}>{de.dispatched_qty}</span> : <span style={{ color: '#E2DFD8' }}>-</span>}</td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              <div className="p-3 border-b" style={{ borderColor: '#E2DFD8' }}>
                                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#5C6B61' }}>
                                  Dealer-wise Breakdown for {product.product_name}
                                  {groupByParent && product.sub_products && product.sub_products.length > 0 && (
                                    <span className="ml-2 normal-case font-normal text-[10px]" style={{ color: '#C05C35' }}>
                                      (direct orders only — sub-brands listed above)
                                    </span>
                                  )}
                                </p>
                              </div>
                              {(!product.dealers || product.dealers.length === 0) ? (
                                <p className="p-4 text-xs italic text-center" style={{ color: '#5C6B61' }}>
                                  No direct {product.product_name} orders in this period — only sub-brand orders (see above).
                                </p>
                              ) : (
                              <table className="w-full">
                                <thead>
                                  <tr style={{ background: '#f5f1e8' }}>
                                    <th className="text-left p-2.5 pl-4 text-xs font-semibold" style={{ color: '#5C6B61' }}>Dealer</th>
                                    <th className="text-left p-2.5 text-xs font-semibold" style={{ color: '#5C6B61' }}>Phone</th>
                                    <th className="text-right p-2.5 text-xs font-semibold" style={{ color: '#5C6B61' }}>Qty</th>
                                    <th className="text-center p-2.5 text-xs font-semibold" style={{ color: '#5C6B61' }}>Pending</th>
                                    <th className="text-center p-2.5 text-xs font-semibold" style={{ color: '#5C6B61' }}>Confirmed</th>
                                    <th className="text-center p-2.5 pr-4 text-xs font-semibold" style={{ color: '#5C6B61' }}>Dispatched</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {product.dealers.map((dealer) => {
                                    const openReview = () => {
                                      // Collect ALL matching orders for this dealer + product (most recent first)
                                      const matches = (orders || [])
                                        .filter(o => o.dealer_id === dealer.dealer_id && (o.items || []).some(i => i.product_id === product.product_id))
                                        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
                                      if (matches.length > 0) {
                                        setPsReviewOrders({ orders: matches, product_id: product.product_id, product_name: product.product_name, dealer_name: dealer.dealer_name });
                                      } else if (onNavigate) {
                                        onNavigate({ type: 'dealer', value: dealer.dealer_id, label: dealer.dealer_name, product_id: product.product_id, auto_review: true });
                                      }
                                    };
                                    return (
                                    <tr key={dealer.dealer_id} style={{ borderBottom: '1px solid #E2DFD8' }} data-testid="ps-dealer-row" className="cursor-pointer hover:bg-[#f5f1e8] transition-colors" onClick={openReview}>
                                      <td className="p-2.5 pl-4 text-sm font-medium">{dealer.dealer_name}</td>
                                      <td className="p-2.5 text-sm" style={{ color: '#5C6B61' }}>{dealer.dealer_phone}</td>
                                      <td className="p-2.5 text-sm text-right font-medium" style={{ color: '#2B5339' }}>{dealer.quantity}</td>
                                      <td className="p-2.5 text-sm text-center">
                                        {dealer.pending_qty > 0 ? <span className="inline-block px-1.5 py-0.5 rounded text-xs" style={{ background: '#E89D4F', color: 'white' }}>{dealer.pending_qty}</span> : <span style={{ color: '#E2DFD8' }}>-</span>}
                                      </td>
                                      <td className="p-2.5 text-sm text-center">
                                        {dealer.confirmed_qty > 0 ? <span className="inline-block px-1.5 py-0.5 rounded text-xs" style={{ background: '#2B5339', color: 'white' }}>{dealer.confirmed_qty}</span> : <span style={{ color: '#E2DFD8' }}>-</span>}
                                      </td>
                                      <td className="p-2.5 pr-4 text-sm text-center">
                                        {dealer.dispatched_qty > 0 ? <span className="inline-block px-1.5 py-0.5 rounded text-xs" style={{ background: '#C05C35', color: 'white' }}>{dealer.dispatched_qty}</span> : <span style={{ color: '#E2DFD8' }}>-</span>}
                                      </td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {data.product_stats.length === 0 && (
                    <tr><td colSpan="6" className="p-6 text-center" style={{ color: '#5C6B61' }}>No orders found for the selected filters</td></tr>
                  )}
                  {/* Totals row */}
                  {data.product_stats.length > 0 && (
                    <tr style={{ background: '#FBF9F6' }} data-testid="ps-totals-row">
                      <td className="p-3"></td>
                      <td className="p-3 text-sm font-bold" style={{ color: '#2B5339' }}>TOTAL</td>
                      <td className="p-3 text-sm text-right font-bold" style={{ color: '#2B5339' }}>{data.product_stats.reduce((s, p) => s + (p.total_quantity || 0), 0)}</td>
                      <td className="p-3 text-sm text-center font-bold" style={{ color: '#E89D4F' }}>{data.product_stats.reduce((s, p) => s + (p.pending_qty || 0), 0)}</td>
                      <td className="p-3 text-sm text-center font-bold" style={{ color: '#2B5339' }}>{data.product_stats.reduce((s, p) => s + (p.confirmed_qty || 0), 0)}</td>
                      <td className="p-3 text-sm text-center font-bold" style={{ color: '#C05C35' }}>{data.product_stats.reduce((s, p) => s + (p.dispatched_qty || 0), 0)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : null}

      {/* Inline Review Dialog — stays on Product Summary */}
      <Dialog open={!!psReviewOrder} onOpenChange={(o) => { if (!o) setPsReviewOrder(null); }}>
        <DialogContent className="max-w-2xl" data-testid="ps-review-dialog">
          <DialogHeader>
            <DialogTitle>Order Details — #{psReviewOrder?.id?.slice(0, 8)}</DialogTitle>
          </DialogHeader>
          {psReviewOrder && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
                  <p className="text-xs" style={{ color: '#5C6B61' }}>Dealer</p>
                  <p className="text-sm font-medium">{psReviewOrder.dealer_name}</p>
                  <p className="text-xs" style={{ color: '#5C6B61' }}>{psReviewOrder.dealer_phone}</p>
                </div>
                <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
                  <p className="text-xs" style={{ color: '#5C6B61' }}>Status</p>
                  <p className="text-sm font-medium uppercase" style={{ color: '#2B5339' }}>{psReviewOrder.status}</p>
                  <p className="text-xs" style={{ color: '#5C6B61' }}>Ordered: {fmtDatePS(psReviewOrder.created_at)}</p>
                </div>
              </div>
              {psReviewOrder.delivery_date && (
                <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
                  <p className="text-xs" style={{ color: '#5C6B61' }}>Delivery Schedule</p>
                  <p className="text-sm">{fmtDatePS(psReviewOrder.delivery_date)} at {psReviewOrder.delivery_time || '-'}</p>
                </div>
              )}
              {psReviewOrder.manual_description && (
                <div className="p-3 rounded border" style={{ borderColor: '#E2DFD8' }}>
                  <p className="text-xs" style={{ color: '#5C6B61' }}>Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{psReviewOrder.manual_description}</p>
                </div>
              )}
              <div className="border rounded" style={{ borderColor: '#E2DFD8' }}>
                <div className="p-2 text-xs font-bold uppercase tracking-wider" style={{ color: '#5C6B61', background: '#FBF9F6', borderBottom: '1px solid #E2DFD8' }}>Items</div>
                <div className="divide-y" style={{ borderColor: '#E2DFD8' }}>
                  {(psReviewOrder.items || []).map((it, i) => (
                    <div key={i} className="flex items-center justify-between p-2 text-sm" style={{ borderColor: '#E2DFD8' }}>
                      <span>{it.product_name}</span>
                      <span style={{ color: '#5C6B61' }}>{it.quantity} {'\u00D7'} {'\u20B9'}{it.price} = <b style={{ color: '#2B5339' }}>{'\u20B9'}{it.total}</b></span>
                    </div>
                  ))}
                </div>
              </div>
              {psReviewOrder.vehicle_number && (
                <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
                  <p className="text-xs" style={{ color: '#5C6B61' }}>Dispatch</p>
                  <p className="text-sm">Vehicle: {psReviewOrder.vehicle_number} · Driver: {psReviewOrder.driver_mobile || '-'}</p>
                </div>
              )}
              <div className="flex justify-between pt-3 font-bold text-lg" style={{ borderTop: '1px solid #E2DFD8' }}>
                <span>Total</span>
                <span style={{ color: '#2B5339' }}>{'\u20B9'}{psReviewOrder.total_amount}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Multi-order drill dialog — lists each order separately for the dealer + product */}
      <Dialog open={!!psReviewOrders} onOpenChange={(o) => { if (!o) setPsReviewOrders(null); }}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="ps-review-orders-dialog">
          <DialogHeader>
            <DialogTitle>
              {psReviewOrders?.dealer_name} — {psReviewOrders?.product_name}
              <span className="ml-2 text-sm font-normal" style={{ color: '#5C6B61' }}>
                ({(psReviewOrders?.orders || []).length} order{(psReviewOrders?.orders || []).length !== 1 ? 's' : ''})
              </span>
            </DialogTitle>
          </DialogHeader>
          {psReviewOrders && (
            <div className="space-y-3">
              {psReviewOrders.orders.map((o) => {
                // When a sub-brand was clicked, restrict the line items shown to that sub-brand's lines only.
                // Otherwise show all line items belonging to the parent product (current behaviour).
                const subFilter = psReviewOrders.sub_brand_filter;
                const prodItems = (o.items || []).filter(i => {
                  if (subFilter) {
                    return (i.parent_product_id === psReviewOrders.product_id) && (i.product_name === subFilter);
                  }
                  return i.product_id === psReviewOrders.product_id;
                });
                const statusColor = o.status === 'dispatched' || o.status === 'delivered'
                  ? '#C05C35'
                  : (o.status === 'confirmed' ? '#2B5339' : '#E89D4F');
                return (
                  <div key={o.id} className="border rounded p-3" style={{ borderColor: '#E2DFD8' }} data-testid="ps-review-order-card">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="text-xs font-bold" style={{ color: '#5C6B61' }}>Order #{o.id.slice(0, 8)}</div>
                        <div className="text-xs" style={{ color: '#5C6B61' }}>Ordered: {fmtDatePS(o.created_at)}</div>
                      </div>
                      <span className="text-xs font-bold uppercase px-2 py-1 rounded" style={{ background: statusColor, color: 'white' }}>
                        {o.status}
                      </span>
                    </div>
                    {prodItems.map((prodItem, pi) => (
                      <div key={pi} className="flex items-center justify-between p-2 text-sm rounded mb-1" style={{ background: '#FBF9F6' }}>
                        <span className="font-medium">
                          {prodItems.length > 1 && <span className="mr-1 text-xs" style={{ color: '#5C6B61' }}>#{pi + 1}</span>}
                          {prodItem.product_name}
                        </span>
                        <span style={{ color: '#5C6B61' }}>
                          {prodItem.quantity} {'\u00D7'} {'\u20B9'}{prodItem.price} = <b style={{ color: '#2B5339' }}>{'\u20B9'}{prodItem.total}</b>
                        </span>
                      </div>
                    ))}
                    {(o.items || []).filter(i => i.product_id !== psReviewOrders.product_id).map((it, j) => (
                      <div key={j} className="flex items-center justify-between px-2 py-1 text-xs mt-1" style={{ color: '#5C6B61' }}>
                        <span>+ {it.product_name}</span>
                        <span>{it.quantity} {'\u00D7'} {'\u20B9'}{it.price} = {'\u20B9'}{it.total}</span>
                      </div>
                    ))}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
                      {o.delivery_date && (
                        <div>
                          <div style={{ color: '#5C6B61' }}>Schedule</div>
                          <div className="font-medium">{fmtDatePS(o.delivery_date)}{o.delivery_time ? ' ' + o.delivery_time : ''}</div>
                        </div>
                      )}
                      {o.vehicle_number && (
                        <>
                          <div>
                            <div style={{ color: '#5C6B61' }}>Vehicle</div>
                            <div className="font-medium">{o.vehicle_number}</div>
                          </div>
                          <div>
                            <div style={{ color: '#5C6B61' }}>Driver</div>
                            <div className="font-medium">{o.driver_mobile || '-'}</div>
                          </div>
                        </>
                      )}
                      <div className="text-right">
                        <div style={{ color: '#5C6B61' }}>Order Total</div>
                        <div className="font-bold" style={{ color: '#2B5339' }}>{'\u20B9'}{o.total_amount}</div>
                      </div>
                    </div>
                    {o.manual_description && (
                      <div className="mt-2 text-xs p-2 rounded" style={{ background: '#FBF9F6', color: '#C05C35', whiteSpace: 'pre-wrap' }}>
                        {o.manual_description}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

